"use client"

import { useCallback, useMemo, useRef, useState } from "react"
import { useApi } from "@/lib/api"
import type {
  VmsRequestListItem,
  VmsEmailSearchParams,
  VmsEmailSearchResult,
} from "@/types/vms/vms-requests"
import type {
  VmsEndpointDto,
  VmsEndpointStatsDto,
  CreateVmsSettingsDto,
  UpdateEndpointDto,
  PromptConfigDto,
  PaginatedVmsSettingsResponse,
  SettingsAuditEntryDto,
} from "@/types/vms/vms-settings"

/**
 * Hook for all VMS API operations.
 * Uses the Workflow Service API (api/vms/...).
 */
export function useVmsApi() {
  const { workflowApi } = useApi()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ─── Requests ───
  //
  // VMS request listing goes through the unified /api/workflow/v2/search
  // endpoint (consumed by the workflow-hub grid) — not a per-entity search
  // route. The old `searchRequests` hook function was removed when that
  // routing landed; per-id and per-action helpers stay below.

  const getRequest = useCallback(
    async (id: string): Promise<VmsRequestListItem | null> => {
      try {
        return await workflowApi.get<VmsRequestListItem>(`/api/vms/requests/${id}`)
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  const updateRequest = useCallback(
    async (id: string, data: Record<string, unknown>) => {
      return workflowApi.put(`/api/vms/requests/${id}`, data)
    },
    [workflowApi]
  )

  const discardRequest = useCallback(
    async (id: string, reason?: string) => {
      return workflowApi.post(`/api/vms/requests/${id}/discard`, { reason })
    },
    [workflowApi]
  )

  const retryParse = useCallback(
    async (id: string) => {
      return workflowApi.post(`/api/vms/requests/${id}/retry-parse`, {})
    },
    [workflowApi]
  )

  /**
   * Eager-create the gw-sync Draft Request for the VMS pre-verification flow.
   * Backend orchestrates the call to gw-sync, stamps PreVerificationRequestId
   * + PreVerificationCreatedAt on the VMS doc, and returns the new gw-sync
   * Request id. The kebab dispatcher then navigates the parent window to
   * /next/mfe/sync/requests/onboarding/{returnedId}.
   *
   * Refuses with 400 if PreVerificationRequestId is already set — the kebab
   * should be showing Resume Pre-verification in that case.
   */
  const startPreVerification = useCallback(
    async (id: string): Promise<{ preVerificationRequestId: string }> => {
      return workflowApi.post<{ preVerificationRequestId: string }>(
        `/api/vms/requests/${id}/start-pre-verification`,
        {}
      )
    },
    [workflowApi]
  )

  // ─── Emails ───

  const searchEmails = useCallback(
    async (params: VmsEmailSearchParams): Promise<VmsEmailSearchResult> => {
      setIsLoading(true)
      setError(null)
      try {
        const result = await workflowApi.post<VmsEmailSearchResult>(
          "/api/vms/emails/search",
          params
        )
        return result
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to search VMS emails"
        setError(msg)
        return { items: [], totalCount: 0, pageNumber: params.pageNumber, pageSize: params.pageSize }
      } finally {
        setIsLoading(false)
      }
    },
    [workflowApi]
  )

  const getEmail = useCallback(
    async (id: string) => {
      return workflowApi.get(`/api/vms/emails/${id}`)
    },
    [workflowApi]
  )

  const deleteEmail = useCallback(
    async (id: string) => {
      return workflowApi.delete(`/api/vms/emails/${id}`)
    },
    [workflowApi]
  )

  /**
   * Soft-deletes a batch of emails. Per-row results come back in the response
   * so the caller can render a partial-success toast — rows linked to a
   * VmsRequest are returned with an error rather than silently skipped.
   * Backed by <c>POST /api/vms/emails/bulk-delete</c>.
   */
  const bulkDeleteEmails = useCallback(
    async (
      ids: string[]
    ): Promise<{
      deletedCount: number
      failedCount: number
      results: Array<{ id: string; success: boolean; error?: string | null }>
    }> => {
      return workflowApi.post(`/api/vms/emails/bulk-delete`, { ids })
    },
    [workflowApi]
  )

  const createRequestFromEmail = useCallback(
    async (emailId: string) => {
      return workflowApi.post(`/api/vms/emails/${emailId}/create-request`, {})
    },
    [workflowApi]
  )

  /**
   * Flips IsSpam = true on the email and, if a VmsRequest was already created
   * from it, cancels that request. Idempotent on repeated calls. Backed by
   * <c>POST /api/vms/emails/{id}/mark-as-spam</c>.
   */
  const markEmailAsSpam = useCallback(
    async (emailId: string) => {
      return workflowApi.post(`/api/vms/emails/${emailId}/mark-as-spam`, {})
    },
    [workflowApi]
  )

  /**
   * Clears IsSpam WITHOUT triggering reprocess. Quiet undo for "I disagree
   * with the classification" — operator hasn't committed to processing yet.
   * Backed by <c>POST /api/vms/emails/{id}/mark-as-valid</c>.
   */
  const markEmailAsValid = useCallback(
    async (emailId: string) => {
      return workflowApi.post(`/api/vms/emails/${emailId}/mark-as-valid`, {})
    },
    [workflowApi]
  )

  /**
   * Affirmative undo: clears IsSpam AND runs the email through the reprocess
   * pipeline. Creates or reactivates the linked VmsRequest. Use when the
   * operator wants to override the spam classification. Backed by
   * <c>POST /api/vms/emails/{id}/move-from-spam-and-process</c>.
   */
  const moveEmailFromSpamAndProcess = useCallback(
    async (emailId: string) => {
      return workflowApi.post(`/api/vms/emails/${emailId}/move-from-spam-and-process`, {})
    },
    [workflowApi]
  )

  /**
   * Re-runs the LLM parse against the linked email and re-populates the
   * VmsRequest's extracted fields. Same backend path as the VmsEmail-side
   * "Reprocess" — the request-side action delegates to the email's
   * reprocessing service. Surfaced separately on the email kebab via
   * <c>moveEmailFromSpamAndProcess</c> for the spam path.
   */
  const reprocessEmail = useCallback(
    async (emailId: string) => {
      return workflowApi.post(`/api/vms/emails/${emailId}/reprocess`, {})
    },
    [workflowApi]
  )

  // ─── Configuration / Endpoints ───

  const getEndpoints = useCallback(
    async (pageNumber = 1, pageSize = 50): Promise<PaginatedVmsSettingsResponse> => {
      try {
        const qs = new URLSearchParams({
          pageNumber: String(pageNumber),
          pageSize: String(pageSize),
        }).toString()
        return await workflowApi.get<PaginatedVmsSettingsResponse>(`/api/vms/config/endpoints?${qs}`)
      } catch {
        return { items: [], totalCount: 0, pageNumber, pageSize }
      }
    },
    [workflowApi]
  )

  const createEndpoint = useCallback(
    async (dto: CreateVmsSettingsDto): Promise<VmsEndpointDto | null> => {
      try {
        return await workflowApi.post<VmsEndpointDto>("/api/vms/config/endpoints", dto)
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  const updateEndpoint = useCallback(
    async (id: string, dto: UpdateEndpointDto): Promise<VmsEndpointDto | null> => {
      try {
        return await workflowApi.put<VmsEndpointDto>(`/api/vms/config/endpoints/${id}`, dto)
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  const deleteEndpoint = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await workflowApi.delete(`/api/vms/config/endpoints/${id}`)
        return true
      } catch {
        return false
      }
    },
    [workflowApi]
  )

  const getEndpointStats = useCallback(
    async (id: string): Promise<VmsEndpointStatsDto | null> => {
      try {
        return await workflowApi.get<VmsEndpointStatsDto>(`/api/vms/config/endpoints/${id}/stats`)
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  /**
   * Loads the audit timeline for a settings group. Returns newest-first; empty
   * array when the group is fully tombstoned or unknown to the caller's tenant.
   * Path takes the SettingsGroupId (stable across versions), not a per-version
   * doc id — the audit timeline is for the logical setting.
   *
   * <p>In-flight requests are deduplicated by settingsGroupId: if two callers
   * (or React strict-mode's double effect invocation in dev) ask for the same
   * id concurrently, the second one gets the in-flight promise instead of
   * firing a second network request. The map entry is cleared when the
   * promise resolves so a follow-up call after data lands still refetches.</p>
   */
  const auditHistoryInFlight = useRef(new Map<string, Promise<SettingsAuditEntryDto[]>>())
  const getEndpointAuditHistory = useCallback(
    async (settingsGroupId: string): Promise<SettingsAuditEntryDto[]> => {
      const existing = auditHistoryInFlight.current.get(settingsGroupId)
      if (existing) return existing

      const promise = workflowApi
        .get<SettingsAuditEntryDto[]>(`/api/vms/config/endpoints/${settingsGroupId}/audit`)
        .catch(() => [] as SettingsAuditEntryDto[])
        .finally(() => {
          auditHistoryInFlight.current.delete(settingsGroupId)
        })

      auditHistoryInFlight.current.set(settingsGroupId, promise)
      return promise
    },
    [workflowApi]
  )

  // ─── Prompt / LLM Configuration ───

  const getPromptConfig = useCallback(
    async (): Promise<PromptConfigDto | null> => {
      try {
        return await workflowApi.get<PromptConfigDto>("/api/vms/config/prompt")
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  const getEmailDomain = useCallback(
    async (): Promise<string | null> => {
      try {
        const result = await workflowApi.get<{ domain: string }>("/api/vms/config/domain")
        return result.domain
      } catch {
        return null
      }
    },
    [workflowApi]
  )

  return useMemo(
    () => ({
      // Requests
      getRequest,
      updateRequest,
      discardRequest,
      retryParse,
      startPreVerification,
      // Emails
      searchEmails,
      getEmail,
      deleteEmail,
      bulkDeleteEmails,
      createRequestFromEmail,
      markEmailAsSpam,
      markEmailAsValid,
      moveEmailFromSpamAndProcess,
      reprocessEmail,
      // Configuration
      getEndpoints,
      createEndpoint,
      updateEndpoint,
      deleteEndpoint,
      getEndpointStats,
      getEndpointAuditHistory,
      // Prompt config (global defaults; per-setting config is in updateEndpoint)
      getPromptConfig,
      getEmailDomain,
      // State — kept on the returned object for API symmetry, but DELIBERATELY
      // NOT included in the memo's dependency array below. These flags flip on
      // every fetch (loading true → loading false / error → null) so including
      // them caused the memoed `vmsApi` to get a new reference per fetch, which
      // busted downstream useCallback / useEffect chains in consumers (the VMS
      // settings panel's loadData useEffect was re-firing on every fetch, which
      // double-called `domain` and `prompt` endpoints). Consumers don't actually
      // read isLoading/error from this hook (grep confirmed zero usages) — they
      // manage their own loading state. Keeping these in the returned object is
      // forward-compat for any future caller, but the memo treats them as
      // implementation-detail.
      isLoading,
      error,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional; see note above.
    [
      getRequest, updateRequest,
      discardRequest, retryParse, startPreVerification,
      searchEmails, getEmail, deleteEmail, bulkDeleteEmails, createRequestFromEmail,
      markEmailAsSpam, markEmailAsValid, moveEmailFromSpamAndProcess, reprocessEmail,
      getEndpoints, createEndpoint, updateEndpoint, deleteEndpoint, getEndpointStats,
      getEndpointAuditHistory,
      getPromptConfig, getEmailDomain,
    ]
  )
}
